import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { generateDebtLetterHtml } from './generateDebtLetterHtml';

const customStyles = {
  content: {
    top: '50%', left: '50%', right: 'auto', bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '700px', maxWidth: '95vw',
    maxHeight: '90vh', overflowY: 'auto',
    borderRadius: '12px', padding: '0',
    border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
  },
  overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1000 },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const readFiles = (files) =>
  Promise.all(
    Array.from(files).map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ dataUrl: reader.result, name: file.name, caption: '' });
          reader.readAsDataURL(file);
        })
    )
  );

const loadLogoBase64 = () =>
  fetch('/smile-logo.png')
    .then((r) => (r.ok ? r.blob() : null))
    .then((blob) => {
      if (!blob) return null;
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    })
    .catch(() => null);

// ─── ImageUploadZone ─────────────────────────────────────────────────────────

function ImageUploadZone({ label, images, onChange }) {
  const inputRef = useRef();

  const handleFiles = async (files) => {
    const newImgs = await readFiles(files);
    onChange([...images, ...newImgs]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const move = (idx, dir) => {
    const arr = [...images];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    onChange(arr);
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  const updateCaption = (idx, value) => {
    const arr = images.map((img, i) => (i === idx ? { ...img, caption: value } : img));
    onChange(arr);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-700">{label}</span>
        <span className="text-xs text-gray-400">{images.length} รูป</span>
      </div>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current.click()}
        className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center cursor-pointer hover:bg-blue-50 transition-colors mb-3"
      >
        <p className="text-blue-500 text-sm">📎 คลิกหรือลากวางรูปภาพที่นี่</p>
        <p className="text-gray-400 text-xs mt-1">รองรับ JPG, PNG, WEBP (หลายรูปได้)</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Image list with captions */}
      {images.length > 0 && (
        <div className="space-y-3">
          {images.map((img, idx) => (
            <div key={idx} className="border rounded-lg overflow-hidden bg-gray-50">
              <div className="flex gap-3 p-2">
                {/* Thumbnail */}
                <div className="flex-shrink-0 w-24 h-20 rounded overflow-hidden border bg-white">
                  <img
                    src={img.dataUrl}
                    alt={img.name}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Right side: name + caption + actions */}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 truncate">
                      {idx + 1}. {img.name}
                    </span>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        title="เลื่อนขึ้น"
                        className="text-xs px-1.5 py-0.5 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-30"
                      >↑</button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === images.length - 1}
                        title="เลื่อนลง"
                        className="text-xs px-1.5 py-0.5 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-30"
                      >↓</button>
                      <button
                        onClick={() => remove(idx)}
                        title="ลบ"
                        className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600 hover:bg-red-200"
                      >✕</button>
                    </div>
                  </div>

                  {/* Caption input */}
                  <div className="mt-1">
                    <input
                      type="text"
                      value={img.caption}
                      onChange={(e) => updateCaption(idx, e.target.value)}
                      placeholder="คำอธิบายรูป (ถ้ามี)"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

function DebtLetterModal({ isOpen, onClose, letterData }) {
  const shopName = useSelector((state) => state.shop.name);
  const [letterName, setLetterName] = useState('');
  const [firstDate, setFirstDate] = useState('');
  const [secondDate, setSecondDate] = useState('');
  const [att2, setAtt2] = useState([]);
  const [att3, setAtt3] = useState([]);
  const [logoBase64, setLogoBase64] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Pre-fill letterName + secondDate from API data; load logo
  useEffect(() => {
    if (!isOpen) return;
    if (letterData?.customer) {
      setLetterName(letterData.customer.customerName || letterData.customer.contact || '');
    }
    if (letterData?.secondDate) {
      const d = new Date(letterData.secondDate);
      setSecondDate(d.toISOString().split('T')[0]);
    }
    loadLogoBase64().then(setLogoBase64);
  }, [isOpen, letterData]);

  const handleClose = () => {
    setLetterName('');
    setFirstDate('');
    setSecondDate('');
    setAtt2([]);
    setAtt3([]);
    onClose();
  };

  const handleGenerate = async () => {
    if (!firstDate) {
      toast.error('กรุณาระบุวันที่ทวงถามครั้งที่ 1');
      return;
    }
    if (!secondDate) {
      toast.error('กรุณาระบุวันที่ทวงถามครั้งที่ 2');
      return;
    }

    setIsGenerating(true);
    try {
      const html = generateDebtLetterHtml({
        letterData,
        att2Images: att2,
        att3Images: att3,
        firstDate,
        secondDate,
        logoBase64,
        letterName: letterName.trim() || (letterData?.customer?.customerName || letterData?.customer?.contact || ''),
        shopName,
        followUpCount: letterData?.followUpCount || 3,
      });

      const win = window.open('', '_blank');
      if (!win) {
        toast.error('Browser บล็อก popup กรุณาอนุญาต popup สำหรับหน้านี้');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (err) {
      console.error(err);
      toast.error('เกิดข้อผิดพลาดในการสร้างเอกสาร');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!letterData) return null;

  const { customer, orders } = letterData;

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} style={customStyles} ariaHideApp={false}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gray-800 rounded-t-xl">
        <h2 className="text-white font-bold text-lg">📄 สร้างจดหมายทวงหนี้</h2>
        <button onClick={handleClose} className="text-gray-300 hover:text-white text-2xl leading-none">×</button>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Customer summary */}
        <div className="bg-blue-50 rounded-lg px-4 py-3 text-sm">
          <p className="font-semibold text-blue-800 mb-1">ข้อมูลลูกค้า</p>
          <p><span className="text-gray-600">ชื่อ:</span> <span className="font-medium">{customer.customerName || customer.contact}</span></p>
          {customer.phone && <p><span className="text-gray-600">โทร:</span> {customer.phone}</p>}
          <p><span className="text-gray-600">จำนวนรายการ:</span> {orders.length} รายการ</p>
          {letterData?.followUpCount != null && (
            <p className="mt-1">
              <span className="text-gray-600">จำนวนครั้งที่ติดตามในระบบ:</span>{' '}
              <span className="font-bold text-blue-700">{letterData.followUpCount} ครั้ง</span>
              <span className="text-gray-400 text-xs ml-1">(จะแสดงในจดหมาย)</span>
            </p>
          )}
        </div>

        {/* Recipient name (editable) */}
        <div>
          <label className="block font-semibold text-gray-700 mb-1">
            ✏️ ชื่อผู้รับจดหมาย (เรียน...)
          </label>
          <input
            type="text"
            value={letterName}
            onChange={(e) => setLetterName(e.target.value)}
            placeholder="ชื่อ-นามสกุลผู้รับ"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <p className="text-xs text-gray-400 mt-1">แก้ไขได้ถ้าต้องการระบุชื่อที่แตกต่างจากในระบบ</p>
        </div>

        {/* Follow-up dates */}
        <div>
          <p className="font-semibold text-gray-700 mb-2">📅 วันที่ทวงถาม</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                ครั้งที่ 1 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={firstDate}
                onChange={(e) => setFirstDate(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                ครั้งที่ 2 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={secondDate}
                onChange={(e) => setSecondDate(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </div>

        {/* Attachment 2 */}
        <div className="border border-gray-200 rounded-lg p-4">
          <ImageUploadZone
            label="📎 เอกสารแนบท้าย 2 — หลักฐานการลงประมูล/สั่งซื้อ"
            images={att2}
            onChange={setAtt2}
          />
        </div>

        {/* Attachment 3 */}
        <div className="border border-gray-200 rounded-lg p-4">
          <ImageUploadZone
            label="📎 เอกสารแนบท้าย 3 — หลักฐานการทวงถามที่ผ่านมา"
            images={att3}
            onChange={setAtt3}
          />
        </div>

        {/* Note about logo */}
        {!logoBase64 && (
          <p className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">
            ⚠️ ไม่พบไฟล์ Logo — วางไฟล์ <code>smile-logo.png</code> ใน <code>public/</code> เพื่อให้ logo แสดงในเอกสาร
          </p>
        )}

        {/* Generate button */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`w-full py-3 rounded-lg font-bold text-white text-base transition-colors ${
            isGenerating ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isGenerating ? 'กำลังสร้าง...' : '🖨️ สร้าง PDF (เปิดหน้าพิมพ์)'}
        </button>

        <p className="text-xs text-gray-400 text-center">
          หน้าเอกสารจะเปิดในแท็บใหม่ → กดปุ่ม "พิมพ์ / บันทึก PDF" → เลือก Save as PDF
        </p>
      </div>
    </Modal>
  );
}

export default DebtLetterModal;
